import { bbox, booleanPointInPolygon, point, pointToPolygonDistance } from '@turf/turf';
import moment from "moment-timezone";
import { utcToZonedTime } from "date-fns-tz";
import { format } from "date-fns";

// Used so that we only fetch this data once, even if mu;tiple requetes are amde for it at close time intervals
let geoDataPromise = null;

export const getGeoData = () => {
  if (geoDataPromise) return geoDataPromise;

  geoDataPromise = fetch('/files/combined-with-oceans-now.json')
    .then(res => {
      if (!res.ok) throw new Error("Could not load timezone data");
      return res.json();
    })
    .then(data => {
      // add simplified bbox to each feature so we can do a quick comapriosn first
      data.features.forEach(feature => {
        if (!feature.bbox) {
          feature.bbox = bbox(feature);
        }
      });
      return data;
    })
    .catch(err => {
      geoDataPromise = null;
      throw err;
    });

  return geoDataPromise;
};

// Calculates squared distance from a point (lon, lat) to a bounding box (minX, minY, maxX, maxY)
// Returns:
// 0 if the point is inside the bbox
// Otherwise the squared distance to the closest edge
// NOTE: Uses squared distance (no Math.sqrt) for speed,
// since we only compare distances, not need the actual value.
const pointToBBoxDistanceSq = (lon, lat, bbox) => {
  const [minX, minY, maxX, maxY] = bbox;

  // Horizontal distance to bbox
  // - 0 if inside horizontally
  // - positive if left or right of box
  const dx =
    lon < minX ? minX - lon :   // point is left of bbox
      lon > maxX ? lon - maxX :   // point is right of bbox
        0;                          // point is inside horizontally

  // Vertical distance to bbox
  // - 0 if inside vertically
  // - positive if below or above box
  const dy =
    lat < minY ? minY - lat :   // point is below bbox
      lat > maxY ? lat - maxY :   // point is above bbox
        0;                          // point is inside vertically

  // Squared Euclidean distance (faster than sqrt)
  // Used only for comparisons, not actual measurement
  return dx * dx + dy * dy;
};

// Returns closest features based on bbox distance
const getTimezonesSortedByDistanceToPoint = (geoJson, lat, lon, limit) => {
  return geoJson.features
    .map(f => ({
      feature: f,
      dist: pointToBBoxDistanceSq(lon, lat, f.bbox)
    }))
    .sort((a, b) => a.dist - b.dist)
    .slice(0, limit);
};

// Finds the timezone that contains the given point
// It first narrows down potential timezones by selecting the 10 closest features based on bounding box distance.
// An exact point-in-polygon check is then run on the potential timezones
// If the  point is no inside any timezones return null
const findExactTimezone = (lat, lon, timezonesOrderedByDistanceToPoint) => {
  const lPoint = point([lon, lat]);
  const candidates = timezonesOrderedByDistanceToPoint.slice(0, 10);

  for (let { feature } of candidates) {
    const match = booleanPointInPolygon(lPoint, feature.geometry);
    if (match) {
      return feature.properties.tzid;
    }
  }

  return null;
};

// Finds the timezone that the gived point is closest to
// It first narrows down potential timezones by selecting the 5 closest features based on bounding box distance.
// An exact distance is the calcualted between the point and the close timezones
// The timezone with the lowest distance is returned
const findClosestTimezone = (lat, lon, timezonesOrderedByDistanceToPoint) => {
  const lPoint = point([lon, lat]);
  const candidates = timezonesOrderedByDistanceToPoint.slice(0, 5);

  let closest = null;
  let minDistance = Infinity;

  for (let { feature } of candidates) {
    const dist = pointToPolygonDistance(lPoint, feature.geometry);

    if (dist < minDistance) {
      minDistance = dist;
      closest = feature;
    }
  }

  return closest?.properties.tzid
};

export const getTimezone = async (lat, lon) => {
  const geoJsonData = await getGeoData();

  if (isValidTZLocationOfPort({ latitude: lat, longitude: lon })) {
    // Timezones ordered by distance to point based on the features bounding box distance.
    const timezonesOrderedByDistanceToPoint = getTimezonesSortedByDistanceToPoint(geoJsonData, lat, lon);

    // Try and find exact match
    const exactTimezone = findExactTimezone(lat, lon, timezonesOrderedByDistanceToPoint);
    if (exactTimezone) {
      return exactTimezone;
    }
    // If no match find closest
    else {
      const closestTimezone = findClosestTimezone(lat, lon, timezonesOrderedByDistanceToPoint);
      return closestTimezone;
    }
  }
  else {
    return null;
  }
};

//#region Conversion functions
const isValidTZLocation = (latitude, longitude) => {
  return (
    latitude !== undefined &&
    longitude !== undefined &&
    latitude !== 0 &&
    longitude !== 0 &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

export const isValidTZLocationOfPort = (port) => {
  return isValidTZLocation(port?.latitude, port?.longitude);
};

export const getPortLocalDatetime = async (port, datetime, _format = "DD/MM/yyyy HH:mm") => {
  if (port && isValidTZLocationOfPort(port)) {
    const timezone = await getTimezone(port?.latitude, port?.longitude);
    return moment.utc(datetime).tz(timezone).format(_format) + " (" + port.portName + " local time)";
  }
  else return "<Unknown local date time since invalid port Location>";
};

export const getPortLocalDatetimeNoLabel = async (port, datetime, _format = "DD/MM/yyyy HH:mm") => {
  if (port && isValidTZLocationOfPort(port)) {
    const timezone = await getTimezone(port?.latitude, port?.longitude);
    return moment.utc(datetime).tz(timezone).format(_format);
  }
  else return "<Unknown local date time since invalid port Location>";
};

export const getPortDateTimeUTC = (port, datetime, _format = "DD/MM/yyyy HH:mm") => {
  if (port && isValidTZLocationOfPort(port)) {
    return moment.utc(datetime).format(_format);
  }
  else return "<Unknown UTC date time since invalid port Location>";
}

export const formatInTimeZone = (date, fmt, tz) => {
  if (!date) return undefined;
  return format(utcToZonedTime(date, tz), fmt, { timeZone: tz });
}
//#endregion